import { ChartManager, DataRow, DataRowExt } from '../chart.manager';

import {
  D3CircleData,
  D3PathData,
  D3PathFilledData,
  d3renderCircle,
  d3renderGradient,
  d3renderPath,
  d3renderPathFilled,
  d3renderRect,
  d3renderText,
} from '../chart.d3';
import { getCurve } from '../chart.utils';

import { roundTo } from '@23degrees/shared-code/utilities';

import chroma from 'chroma-js';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import { Delaunay } from 'd3-delaunay';
import { line } from 'd3-shape';
import { voronoi } from 'd3-voronoi';
import { overlayText } from '../chart.overlay';
import { color2css } from '../utilities/utils';

export const renderLine = (chartManager: ChartManager): void => {
  const { typeSpecific } = chartManager.content;
  const { aggregationField, valueField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, lines } = typeSpecific.chart;

  const { margin, padding } = typeSpecific.chart.style;
  const offsetTop = chartManager.getStyleInPx(margin, 'top') + chartManager.getStyleInPx(padding, 'top');
  const offsetLeft = chartManager.getStyleInPx(margin, 'left') + chartManager.getStyleInPx(padding, 'left');

  const getDimensionValue = (obj: DataRowExt) => {
    const { dataType } = typeSpecific.fields[aggregationField];
    if (dataType === 'date') return chartManager.xScale(obj['~meta'].moment);
    return roundTo(chartManager.xScale(obj[aggregationField]) + chartManager.xScaleBandWidth() / 2);
  };

  let safeData = chartManager.data;
  const { dataType } = typeSpecific.fields[aggregationField];
  if (dataType === 'date') {
    safeData = chartManager.data.filter((row) => row['~meta'].moment.isValid());
  }

  const curve = getCurve(lines.curve);
  const lineFn = line<DataRowExt>()
    .curve(curve)
    .x((obj) => getDimensionValue(obj))
    .y((obj) => chartManager.yScale(obj[valueField]));

  const data: D3PathData[] = [];
  const circleData: D3CircleData[] = [];

  for (const group of chartManager.dataGroup) {
    const color = chartManager.groupedColorScale(group['~meta']);
    const hoverColor = color2css(chroma(color).darken());

    const path = lineFn(group[1]);
    const animate = {
      enabled: animation.enabled,
      delay: animation.delay,
      duration: animation.duration,
      color: color2css(color),
      path: path,
      width: lines.thinkness,
    };

    data.push({ color: color2css(color), path: path, width: lines.thinkness, animate: animate });

    if (!lines.dots.enabled) continue;
    if (group[1].length > lines.dots.maxDots) continue;

    for (const dot of group[1]) {
      const x = getDimensionValue(dot);
      const y = chartManager.yScale(dot[valueField]);
      const dotColor = chroma(lines.dots.color).alpha() > 0 ? color2css(lines.dots.color) : color2css(color);
      circleData.push({
        x: roundTo(x),
        y: roundTo(y),
        color: dotColor,
        radius: lines.dots.size ?? 3,
        stroke: lines.dots.outlineColor,
        strokeWidth: lines.dots.outlineWidth,
      });
    }
  }

  const voronoiData: D3PathFilledData[] = [];

  // https://github.com/d3/d3-delaunay/issues/79
  const jitter = () => Math.random() / 1000;
  const fx = (obj) => getDimensionValue(obj) + jitter();
  const fy = (obj) => chartManager.yScale(obj[valueField]) + jitter();

  try {
    const delaunay = Delaunay.from(safeData, fx, fy);
    const voronoi = delaunay.voronoi([0, 0, chartManager.chartWidth, chartManager.chartHeight]);

    // fast delaunay / voronoi worked
    for (let idx = 0; idx < safeData.length; idx++) {
      const path = voronoi.renderCell(idx);
      if (!path || path?.includes('NaN')) {
        continue;
      }

      const click = (event, obj) => {
        const clickData = safeData[obj.idx];
        const x = getDimensionValue(clickData) + chartManager.yAxisWidth + offsetLeft;
        const y = chartManager.yScale(clickData[valueField]) + offsetTop;

        chartManager.openPopup(event, clickData, [x, y]);
        chartManager.mouseClick(event, clickData, [x, y]);
      };

      const enter = (event, obj, node) => chartManager.mouseEnter(event, safeData[obj.idx]);
      const leave = (event, obj, node) => chartManager.mouseLeave(event, safeData[obj.idx]);
      const move = (event, obj, node) => {
        const clickData = safeData[obj.idx];
        const x = getDimensionValue(clickData) + chartManager.yAxisWidth + offsetLeft;
        const y = chartManager.yScale(clickData[valueField]) + offsetTop;
        chartManager.mouseMove(event, safeData[obj.idx], [x, y]);
      };

      const callbacks = { click, enter, leave, move };

      voronoiData.push({ idx: idx, color: 'none', path, callbacks }); // stroke: 'red'
    }
  } catch (error) {
    // suppress
    console.log('[delaunay] error', error);
  }

  try {
    // fallback voronoi
    if (voronoiData.length !== safeData.length) {
      voronoiData.splice(0, voronoiData.length); // reset array
      console.log('[Chart]', 'Falling back to d3-voronoi ..');
      const voronoiFn = voronoi()
        .x(fx)
        .y(fy)
        .extent([
          [0, 0],
          [chartManager.chartWidth, chartManager.chartHeight],
        ]);

      const polygons = voronoiFn.polygons(safeData);
      for (const obj of polygons) {
        if (!obj) continue;
        const path = `M${obj.join('L')}Z`;

        const click = (event) => {
          const x = getDimensionValue(obj.data) + chartManager.yAxisWidth + offsetLeft;
          const y = chartManager.yScale(obj.data[valueField]) + offsetTop;
          chartManager.openPopup(event, obj.data, [x, y]);
          chartManager.mouseClick(event, obj.data, [x, y]);
        };

        const enter = (event, _, node) => chartManager.mouseEnter(event, obj.data);
        const leave = (event, _, node) => chartManager.mouseLeave(event, obj.data);
        const move = (event, _, node) => {
          const x = getDimensionValue(obj.data) + chartManager.yAxisWidth + offsetLeft;
          const y = chartManager.yScale(obj.data[valueField]) + offsetTop;
          chartManager.mouseMove(event, obj.data, [x, y]);
        };

        const callbacks = { click, enter, leave, move };

        voronoiData.push({ color: 'none', path, callbacks }); // stroke: 'red'
      }
    }
  } catch (error) {
    // suppress
    console.log('[voronoi] error', error);
  }

  // clear
  d3renderRect(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  d3renderPath(chartManager.chart.element, data, '.chart', chartManager.animationStop);
  d3renderPathFilled(chartManager.chart.element, voronoiData, '.voronoi');
  d3renderCircle(chartManager.chart.element, circleData, '.chart');

  overlayText(chartManager.overlay, [], '.overlay-values');
};
